// frozen_string_literal: true

import { Controller } from "@hotwired/stimulus";

/**
 * Popover Menu Controller
 *
 * This controller manages popover menus throughout the application.
 *
 * @example
 * <div data-controller="popover-menu">
 *   <button data-action="click->popover-menu#toggle" data-popover-menu-target="button">
 *     <!-- Menu trigger icon -->
 *   </button>
 *   <div data-popover-menu-target="menu" class="hidden">
 *     <!-- Menu content -->
 *   </div>
 * </div>
 */
export default class extends Controller {
  static targets = ["menu", "button"];

  connect() {
    console.log("Popover Menu Controller connected");
    // Close the menu when clicking outside
    this.outsideClickHandler = this.handleOutsideClick.bind(this);
    document.addEventListener("click", this.outsideClickHandler);

    // Close the menu when pressing escape
    this.escapeHandler = this.handleEscape.bind(this);
    document.addEventListener("keydown", this.escapeHandler);

    this.positionMenu();
  }

  disconnect() {
    document.removeEventListener("click", this.outsideClickHandler);
    document.removeEventListener("keydown", this.escapeHandler);
  }

  toggle(event) {
    console.log("Toggle event triggered");
    event.stopPropagation();
    this.menuTarget.classList.toggle("hidden");

    // Set aria-expanded attribute for accessibility
    const isExpanded = !this.menuTarget.classList.contains("hidden");
    this.buttonTarget.setAttribute("aria-expanded", isExpanded);

    // Position the menu
    if (isExpanded) {
      this.positionMenu();
    }
  }

  open() {
    if (this.menuTarget.classList.contains("hidden")) {
      this.menuTarget.classList.remove("hidden");
      this.buttonTarget.setAttribute("aria-expanded", true);
      this.positionMenu();
    }
  }

  close() {
    if (!this.menuTarget.classList.contains("hidden")) {
      this.menuTarget.classList.add("hidden");
      this.buttonTarget.setAttribute("aria-expanded", false);
    }
  }

  positionMenu() {
    // Get button position
    const buttonRect = this.buttonTarget.getBoundingClientRect();
    const menuRect = this.menuTarget.getBoundingClientRect();

    // Default position is below and right-aligned
    let top = buttonRect.bottom + window.scrollY;
    let right = window.innerWidth - buttonRect.right - window.scrollX;

    // Check if menu would go off-screen to the left
    if (buttonRect.right - menuRect.width < 0) {
      right = window.innerWidth - buttonRect.left - window.scrollX;
    }

    // Check if menu would go off-screen to the bottom
    if (top + menuRect.height > window.innerHeight + window.scrollY) {
      top = buttonRect.top - menuRect.height + window.scrollY;
    }

    // Apply the positioning
    this.menuTarget.style.position = "absolute";
    this.menuTarget.style.top = `${top}px`;
    this.menuTarget.style.right = `${right}px`;
    this.menuTarget.style.zIndex = "50";
  }

  handleOutsideClick(event) {
    if (
      !this.menuTarget.classList.contains("hidden") &&
      !this.menuTarget.contains(event.target) &&
      !this.buttonTarget.contains(event.target)
    ) {
      this.close();
    }
  }

  handleEscape(event) {
    if (
      event.key === "Escape" &&
      !this.menuTarget.classList.contains("hidden")
    ) {
      this.close();
    }
  }
}
